/**
 * ============================================================
 * PLATFORM FEE PLANS PAGE
 * ============================================================
 * Página para gerenciar planos de taxa da plataforma
 * Autor: Sistema Pagmus
 * Data: 2025-11-06
 * ============================================================
 */

import React, { useState, useEffect } from 'react';
import { Button, Badge } from 'react-bootstrap';
import { Icon } from '@iconify/react';
import { API_URL } from '../../config/api';
import MasterLayout from '../../masterLayout/MasterLayout';
import AdminPageHeader from '../../components/common/AdminPageHeader';
import StatCard from '../../components/common/StatCard';
import './PlatformFeePlansPage.scss';
import FormModal from '../../components/modals/FormModal';
import ConfirmationModal from '../../components/modals/ConfirmationModal';
import CurrencyInput from '../../components/forms/CurrencyInput';
import ModernSwitch from '../../components/forms/ModernSwitch';

const PlatformFeePlansPage = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [confirmationConfig, setConfirmationConfig] = useState({});
  const [editingPlan, setEditingPlan] = useState(null);
  const [formData, setFormData] = useState({
    plan_code: '',
    plan_name: '',
    plan_description: '',
    fixed_fee_cents: 0,
    percentage_fee: 0,
    is_active: true
  });

  useEffect(() => {
    loadPlans();
  }, []);

  const loadPlans = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/platform-fee-plans`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      if (data.success) {
        setPlans(data.data);
      }
    } catch (error) {
      console.error('Erro ao carregar planos:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCreateModal = () => {
    setEditingPlan(null);
    setFormData({
      plan_code: '',
      plan_name: '',
      plan_description: '',
      fixed_fee_cents: 0,
      percentage_fee: 0,
      is_active: true
    });
    setShowModal(true);
  };

  const handleOpenEditModal = (plan) => {
    setEditingPlan(plan);
    setFormData({
      plan_code: plan.plan_code,
      plan_name: plan.plan_name,
      plan_description: plan.plan_description || '',
      fixed_fee_cents: plan.fixed_fee_cents,
      percentage_fee: plan.percentage_fee,
      is_active: plan.is_active
    });
    setShowModal(true);
  };

  const handleSavePlan = async () => {
    try {
      const token = localStorage.getItem('token');
      const url = editingPlan
        ? `${API_URL}/api/admin/platform-fee-plans/${editingPlan.id}`
        : `${API_URL}/api/admin/platform-fee-plans`;

      const method = editingPlan ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();
      if (data.success) {
        setShowModal(false);
        loadPlans();
      } else {
        alert(data.message || 'Erro ao salvar plano');
      }
    } catch (error) {
      console.error('Erro ao salvar plano:', error);
      alert('Erro ao salvar plano');
    }
  };

  const handleSetDefault = (plan) => {
    setConfirmationConfig({
      title: 'Definir Plano Padrão',
      message: `Tem certeza que deseja definir "${plan.plan_name}" como plano padrão? O plano padrão atual será substituído.`,
      onConfirm: async () => {
        try {
          const token = localStorage.getItem('token');
          const response = await fetch(`${API_URL}/api/admin/platform-fee-plans/${plan.id}/set-default`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });

          const data = await response.json();
          if (data.success) {
            setShowConfirmation(false);
            loadPlans();
          } else {
            alert(data.message || 'Erro ao definir plano padrão');
          }
        } catch (error) {
          console.error('Erro ao definir plano padrão:', error);
          alert('Erro ao definir plano padrão');
        }
      }
    });
    setShowConfirmation(true);
  };

  const handleDeactivate = (plan) => {
    setConfirmationConfig({
      title: 'Desativar Plano',
      message: `Tem certeza que deseja desativar o plano "${plan.plan_name}"? Ele não poderá mais ser atribuído a novos usuários.`,
      onConfirm: async () => {
        try {
          const token = localStorage.getItem('token');
          const response = await fetch(`${API_URL}/api/admin/platform-fee-plans/${plan.id}/deactivate`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });

          const data = await response.json();
          if (data.success) {
            setShowConfirmation(false);
            loadPlans();
          } else {
            alert(data.message || 'Erro ao desativar plano');
          }
        } catch (error) {
          console.error('Erro ao desativar plano:', error);
          alert('Erro ao desativar plano');
        }
      }
    });
    setShowConfirmation(true);
  };

  return (
    <MasterLayout>
      <div className="dashboard-main-body">
        <div className="container-fluid p-4">
          <AdminPageHeader
            icon="mdi:cash-multiple"
            title="Taxa da Plataforma"
            subtitle="Gerencie os planos de taxa que podem ser atribuídos aos produtores"
          />

          <div className="platform-fee-plans-page">
            {/* Estatísticas */}
            <div className="row g-3" style={{ marginBottom: '24px' }}>
              <div className="col-xxl-4 col-sm-6">
                <StatCard
                  variant="blue"
                  title="Total de Planos"
                  value={plans.length}
                  change="Planos cadastrados"
                  icon="mdi:package-variant"
                />
              </div>
              <div className="col-xxl-4 col-sm-6">
                <StatCard
                  variant="green"
                  title="Planos Ativos"
                  value={plans.filter(p => p.is_active).length}
                  change="Planos disponíveis"
                  icon="mdi:check-circle"
                />
              </div>
              <div className="col-xxl-4 col-sm-6">
                <StatCard
                  variant="purple"
                  title="Plano Padrão"
                  value={plans.find(p => p.is_default)?.plan_name || 'N/A'}
                  change="Plano padrão do sistema"
                  icon="mdi:star"
                />
              </div>
            </div>

            {/* Botão Novo Plano */}
            <div className="d-flex justify-content-end mb-3">
              <Button variant="primary" onClick={handleOpenCreateModal}>
                <Icon icon="mdi:plus" className="me-2" />
                Novo Plano
              </Button>
            </div>

            {/* Lista de Planos em Cards */}
            <div className="platform-fee-plans-page__content">
              {loading ? (
                <div className="loading-state">
                  <span className="loading-state__spinner">⏳</span>
                  <span className="loading-state__text">Carregando planos...</span>
                </div>
              ) : plans.length === 0 ? (
                <div className="empty-state">
                  <span className="empty-state__icon">📭</span>
                  <span className="empty-state__text">Nenhum plano cadastrado.</span>
                </div>
              ) : (
                <div className="plans-list">
                  {plans.map((plan) => (
                    <div key={plan.id} className="plan-card">
                      <div className="plan-card__avatar">
                        <span className="plan-card__avatar-text">
                          {plan.plan_code?.charAt(0)?.toUpperCase() || 'P'}
                        </span>
                      </div>
                      
                      <div className="plan-card__info">
                        <div className="plan-card__name">
                          {plan.plan_name}
                          {plan.is_default && (
                            <Badge bg="warning" className="ms-2">
                              ⭐ Padrão
                            </Badge>
                          )}
                        </div>
                        <div className="plan-card__code">
                          Código: <code>{plan.plan_code}</code>
                        </div>
                        {plan.plan_description && (
                          <div className="plan-card__description">
                            {plan.plan_description}
                          </div>
                        )}
                      </div>

                      <div className="plan-card__stats">
                        <div className="plan-card__stat">
                          <span className="plan-card__stat-value">
                            R$ {(plan.fixed_fee_cents / 100).toFixed(2)}
                          </span>
                          <span className="plan-card__stat-label">Taxa Fixa</span>
                        </div>
                        <div className="plan-card__stat">
                          <span className="plan-card__stat-value">
                            {plan.percentage_fee}%
                          </span>
                          <span className="plan-card__stat-label">Taxa %</span>
                        </div>
                        <div className="plan-card__stat">
                          <span className="plan-card__stat-value">
                            {plan.is_active ? (
                              <Badge bg="success">Ativo</Badge>
                            ) : (
                              <Badge bg="secondary">Inativo</Badge>
                            )}
                          </span>
                          <span className="plan-card__stat-label">Status</span>
                        </div>
                      </div>

                      <div className="plan-card__actions">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => handleOpenEditModal(plan)}
                        >
                          <Icon icon="mdi:pencil" className="me-1" />
                          Editar
                        </Button>
                        {!plan.is_default && (
                          <Button
                            variant="outline-warning"
                            size="sm"
                            onClick={() => handleSetDefault(plan)}
                          >
                            <Icon icon="mdi:star" className="me-1" />
                            Padrão
                          </Button>
                        )}
                        {!plan.is_default && plan.is_active && (
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleDeactivate(plan)}
                          >
                            <Icon icon="mdi:close" className="me-1" />
                            Desativar
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Modal de Criação/Edição */}
          <FormModal
            show={showModal}
            onHide={() => setShowModal(false)}
            title={editingPlan ? 'Editar Plano' : 'Novo Plano'}
            onSave={handleSavePlan}
            size="lg"
          >
            <div className="row g-3">
              <div className="col-md-6">
                <label className="form-label">Código do Plano *</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Ex: VIP, PREMIUM"
                  value={formData.plan_code}
                  onChange={(e) => setFormData({ ...formData, plan_code: e.target.value.toUpperCase() })}
                  disabled={editingPlan !== null}
                />
              </div>

              <div className="col-md-6">
                <label className="form-label">Nome do Plano *</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Ex: Clientes VIP"
                  value={formData.plan_name}
                  onChange={(e) => setFormData({ ...formData, plan_name: e.target.value })}
                />
              </div>

              <div className="col-md-12">
                <label className="form-label">Descrição</label>
                <textarea
                  className="form-control"
                  rows={3}
                  placeholder="Descrição do plano..."
                  value={formData.plan_description}
                  onChange={(e) => setFormData({ ...formData, plan_description: e.target.value })}
                />
              </div>

              <div className="col-md-6">
                <label className="form-label">Taxa Fixa (R$) *</label>
                <CurrencyInput
                  value={formData.fixed_fee_cents / 100}
                  onChange={(value) => setFormData({ ...formData, fixed_fee_cents: Math.round(value * 100) })}
                  placeholder="R$ 0,00"
                />
              </div>

              <div className="col-md-6">
                <label className="form-label">Taxa Percentual (%) *</label>
                <input
                  type="number"
                  className="form-control"
                  placeholder="0.00"
                  step="0.01"
                  min="0"
                  max="100"
                  value={formData.percentage_fee}
                  onChange={(e) => setFormData({ ...formData, percentage_fee: parseFloat(e.target.value) || 0 })}
                />
              </div>

              <div className="col-md-12">
                <ModernSwitch
                  label="Plano Ativo"
                  checked={formData.is_active}
                  onChange={(value) => setFormData({ ...formData, is_active: value })}
                  description="Apenas planos ativos podem ser atribuídos a novos usuários"
                />
              </div>
            </div>
          </FormModal>

          {/* Modal de Confirmação */}
          <ConfirmationModal
            show={showConfirmation}
            onHide={() => setShowConfirmation(false)}
            title={confirmationConfig.title}
            message={confirmationConfig.message}
            onConfirm={confirmationConfig.onConfirm}
          />
        </div>
      </div>
    </MasterLayout>
  );
};

export default PlatformFeePlansPage;

